import React, { useEffect, useState, useRef } from 'react';
import { FaDrawPolygon, FaChartLine, FaRegCircle } from 'react-icons/fa';
import { MdOutlineCancel, MdUndo, MdCropSquare, MdClearAll } from 'react-icons/md';
import { TbPointFilled, TbLassoPolygon, TbMapPinSearch, TbPresentation } from 'react-icons/tb';
import { PiCursorClick } from 'react-icons/pi';
import { LuUtilityPole } from "react-icons/lu";
import { BsBadge3D } from "react-icons/bs";
import { IoCheckmarkDoneOutline } from 'react-icons/io5';
import { GiConfirmed } from "react-icons/gi";
import TerrainChart from '../Charts/TerrainChart';
import DynamicModal from '../Modal/DynamicModal';
import './DrawingToolbar.css';
import { useAccessRights } from '../../utils/LoginForm/Auth/useAccessRights';
import { ReactComponent as SubstationIcon } from '../../assets/icons/transformer-power-voltage-energy-electronic-svgrepo-com.svg';
import { ReactComponent as PowerPoleIcon } from '../../assets/icons/power-pole.svg';
import { ReactComponent as TrajectoryIcon } from '../../assets/icons/navigation-trajectory-svgrepo-com.svg';

const SELECT_MODES = ['select', 'selectRectangle', 'selectCircle'];

const iconMap = {
    Point: (layerName) => {
        const pointIcons = {
            'stubovi-layer': <LuUtilityPole style={{ width: '20px', height: '20px' }} title="Stub" />,
            'trafo_stanice-layer': <SubstationIcon style={{ width: '20px', height: '20px' }} title="Trafostanica" />,
            'mjerna_mjesta-layer': <TbPointFilled style={{ width: '20px', height: '20px' }} title="Mjerno mjesto" />
        };

        return pointIcons[layerName] || <TbPointFilled style={{ width: '20px', height: '20px' }} title="Tačka" />;
    },
    LineString: <TrajectoryIcon style={{ width: '20px', height: '20px' }} title="Očitački hod" />,
    MultiLineString: <PowerPoleIcon style={{ width: '20px', height: '20px' }} title="Vod" />,
    Polygon: <FaDrawPolygon style={{ width: '20px', height: '20px' }} title="Poligon" />,
    MultiPolygon: <FaDrawPolygon style={{ width: '20px', height: '20px' }} title="Višestruki poligon" />
};

const DrawingToolbar = ({
    layerInfo,
    activeMode,
    setActiveMode,
    onChange,
    viewState,
    setViewState,
    setGeometryType,
    handleCompleteDrawing,
    handleUndoDrawing,
    handleFinish,
    handleCancel,
    terrainProfilePoints,
    setTerrainProfilePoints,
    isLayersExists,
    showSearch,
    setShowSearch,
    handleCancelDrawing,
    saveSplitLine,
    handleClearSelectedFeatures
}) => {
    const selectPopoverRef = useRef(null);

    const [show3DButton, setShow3DButton] = useState(false);
    const [is3D, setIs3D] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedMode, setSelectedMode] = useState(null);
    const [selectedLayer, setSelectedLayer] = useState(null);
    const [coordinateMode, setCoordinateMode] = useState(false);
    const [longitude, setLongitude] = useState('');
    const [latitude, setLatitude] = useState('');
    const [isTerrainModalOpen, setIsTerrainModalOpen] = useState(false);
    const [showSelectOptions, setShowSelectOptions] = useState(false);

    const availableGeometryTypes = {};

    layerInfo?.forEach(layer => {
        layer.geometryType?.forEach(type => {
            if (!availableGeometryTypes[type]) {
                availableGeometryTypes[type] = [];
            }

            availableGeometryTypes[type].push(layer.name);
        });
    });

    const { accessProfile } = useAccessRights();

    const canWrite = accessProfile.canWrite.value || accessProfile.canWriteOrgJedinicu.value;
    const isOH = accessProfile.canWriteOH.value;
    const canUseAllExceptOH = !isOH;

    const isSelectActive = SELECT_MODES.includes(activeMode);
    const isAnotherModeActive = activeMode && !isSelectActive;

    const canSwitchFromCurrentMode = (targetMode) => {
        if (!activeMode) return true;
        if (SELECT_MODES.includes(activeMode)) return true;
        return activeMode === targetMode;
    };

    const isDrawingLayerButtonActive = (geom, layerName) => {
        return (
            activeMode?.startsWith(`add${geom}`) &&
            selectedLayer === layerName
        );
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                showSelectOptions &&
                selectPopoverRef.current &&
                !selectPopoverRef.current.contains(event.target)
            ) {
                setShowSelectOptions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showSelectOptions]);

    useEffect(() => {
        const closeToolbarPopups = () => {
            setShowSelectOptions(false);
        };

        window.addEventListener("close-toolbar-popups", closeToolbarPopups);

        return () => {
            window.removeEventListener("close-toolbar-popups", closeToolbarPopups);
        };
    }, []);

    const openModal = (mode, layerName, geometryType) => {
        setSelectedMode(mode);
        setSelectedLayer(layerName);
        setGeometryType(geometryType);
        setModalIsOpen(true);
        setCoordinateMode(false);
        setShowSelectOptions(false);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setLongitude('');
        setLatitude('');
    };

    const handleModeChange = (drawingType) => {
        if (drawingType === 'coordinates') {
            setCoordinateMode(true);
            return;
        }

        const finalMode = `${selectedMode}-${drawingType}`;
        setActiveMode(finalMode);

        const selectedLayerInfo = layerInfo?.find(layer => layer.name === selectedLayer);

        if (onChange) {
            onChange({
                activeMode: finalMode,
                selectedLayer: selectedLayerInfo || null
            });
        }

        closeModal();
    };

    const handleCoordinateSubmit = () => {
        if (!longitude || !latitude) return;

        const finalMode = `${selectedMode}-coordinates`;
        setActiveMode(finalMode);

        if (onChange) {
            onChange({
                activeMode: finalMode,
                selectedLayer: layerInfo?.find(layer => layer.name === selectedLayer) || null,
                coordinates: [parseFloat(longitude), parseFloat(latitude)]
            });
        }

        closeModal();
    };

    useEffect(() => {
        if (viewState.zoom <= 15) {
            setShow3DButton(false);
            setIs3D(false);
            setViewState(prev => ({
                ...prev,
                pitch: 0,
                bearing: 0,
                is3D: false
            }));
        } else {
            setShow3DButton(true);

            if (is3D) {
                setViewState(prev => ({
                    ...prev,
                    is3D: true
                }));
            }
        }
    }, [viewState.zoom, setViewState, is3D]);

    const toggle3DView = () => {
        setIs3D(prevIs3D => {
            const newIs3D = !prevIs3D;

            setViewState(prev => ({
                ...prev,
                pitch: newIs3D ? 60 : 0,
                bearing: newIs3D ? 30 : 0,
                is3D: newIs3D
            }));

            return newIs3D;
        });
    };

    return (
        <div className="drawing-toolbar">
            <div ref={selectPopoverRef} style={{ position: "relative" }}>
                <button
                    className={isSelectActive ? 'active' : ''}
                    onClick={() => {
                        setShowSelectOptions(prev => !prev);

                        if (!isSelectActive) {
                            setActiveMode('select');
                        }
                    }}
                    title="Odabir"
                >
                    <PiCursorClick />
                </button>

                {showSelectOptions && (
                    <div className="select-options-popover">
                        <button
                            className={activeMode === 'select' ? 'active' : ''}
                            onClick={() => {
                                setActiveMode('select');
                                setShowSelectOptions(false);
                            }}
                            title="Obični odabir"
                        >
                            <PiCursorClick />
                        </button>

                        <button
                            className={activeMode === 'selectRectangle' ? 'active' : ''}
                            onClick={() => {
                                setActiveMode('selectRectangle');
                                setShowSelectOptions(false);
                            }}
                            title="Odabir kroz kvadrat — Shift + drag"
                        >
                            <MdCropSquare />
                        </button>

                        <button
                            className={activeMode === 'selectCircle' ? 'active' : ''}
                            onClick={() => {
                                setActiveMode('selectCircle');
                                setShowSelectOptions(false);
                            }}
                            title="Odabir kroz krug — Shift + drag"
                        >
                            <FaRegCircle />
                        </button>
                    </div>
                )}
            </div>

            {isSelectActive && (
                <button
                    onClick={() => {
                        if (typeof handleClearSelectedFeatures === "function") {
                            handleClearSelectedFeatures();
                        }

                        setShowSelectOptions(false);
                    }}
                    title="Poništi sve selektovane objekte"
                >
                    <MdClearAll />
                </button>
            )}

            {isLayersExists && (
                <>
                    {activeMode === 'splitLine' && (
                        <button
                            className="active"
                            onClick={saveSplitLine}
                            title="Sačuvaj izmjene linije"
                        >
                            <GiConfirmed />
                        </button>
                    )}

                    <button
                        className={activeMode === 'regionOfInterest' ? 'active' : ''}
                        onClick={() => {
                            if (!canUseAllExceptOH || !canSwitchFromCurrentMode('regionOfInterest')) return;

                            setActiveMode(prev => prev === 'regionOfInterest' ? null : 'regionOfInterest');
                            setShowSelectOptions(false);
                        }}
                        disabled={!canUseAllExceptOH || !canSwitchFromCurrentMode('regionOfInterest')}
                        style={{
                            cursor: !canUseAllExceptOH || !canSwitchFromCurrentMode('regionOfInterest') ? 'not-allowed' : 'pointer',
                            opacity: !canUseAllExceptOH || !canSwitchFromCurrentMode('regionOfInterest') ? 0.5 : 1
                        }}
                        title='Region od interesa'
                    >
                        <TbLassoPolygon />
                    </button>

                    {activeMode === "regionOfInterest" && (
                        <>
                            <button onClick={handleFinish} title='Završi crtanje regiona'>
                                <GiConfirmed />
                            </button>

                            <button onClick={handleCancel} title='Poništi crtanje regiona'>
                                <MdOutlineCancel />
                            </button>
                        </>
                    )}

                    {show3DButton && (
                        <button
                            className={`toggle-3d-btn ${is3D ? 'active' : ''}`}
                            onClick={toggle3DView}
                            title='3D prikaz'
                            disabled={
                                activeMode &&
                                ![
                                    'select',
                                    'selectRectangle',
                                    'selectCircle',
                                    'update',
                                    'regionOfInterest'
                                ].includes(activeMode)
                            }
                            style={{
                                cursor:
                                    activeMode &&
                                        ![
                                            'select',
                                            'selectRectangle',
                                            'selectCircle',
                                            'update',
                                            'regionOfInterest'
                                        ].includes(activeMode)
                                        ? 'not-allowed'
                                        : 'pointer',
                                opacity:
                                    activeMode &&
                                        ![
                                            'select',
                                            'selectRectangle',
                                            'selectCircle',
                                            'update',
                                            'regionOfInterest'
                                        ].includes(activeMode)
                                        ? 0.5
                                        : 1
                            }}
                        >
                            <BsBadge3D />
                        </button>
                    )}
                </>
            )}

            {['Point', 'LineString', 'MultiLineString', 'Polygon', 'MultiPolygon'].map(geom => (
                availableGeometryTypes[geom]?.map(layerName => {
                    const targetMode = `add${geom}-${layerName}`;
                    const disabled = !canWrite || !canSwitchFromCurrentMode(targetMode);

                    return (
                        <button
                            key={`${geom}-${layerName}`}
                            className={isDrawingLayerButtonActive(geom, layerName) ? 'active' : ''}
                            onClick={() => {
                                if (disabled) return;

                                setShowSelectOptions(false);
                                openModal(`add${geom}`, layerName, geom);
                            }}
                            disabled={disabled}
                            style={{
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                opacity: disabled ? 0.5 : 1
                            }}
                            title={layerName}
                        >
                            {typeof iconMap[geom] === 'function' ? iconMap[geom](layerName) : iconMap[geom]}
                        </button>
                    );
                })
            ))}

            {activeMode?.startsWith('add') && (
                <>
                    <button onClick={handleCompleteDrawing} title='Završi crtanje'>
                        <IoCheckmarkDoneOutline />
                    </button>

                    <button onClick={handleUndoDrawing} title="Poništi posljednju tačku">
                        <MdUndo />
                    </button>

                    <button onClick={handleCancelDrawing} title='Poništi crtež'>
                        <MdOutlineCancel />
                    </button>
                </>
            )}

            <button
                className={activeMode === 'terrainCrossSection' ? 'active' : ''}
                onClick={() => {
                    if (!canSwitchFromCurrentMode('terrainCrossSection')) return;

                    setShowSelectOptions(false);
                    setActiveMode(prev => prev === 'terrainCrossSection' ? null : 'terrainCrossSection');
                }}
                title="Dijagram presjek terena"
                disabled={!canSwitchFromCurrentMode('terrainCrossSection')}
                style={{
                    cursor: !canSwitchFromCurrentMode('terrainCrossSection') ? 'not-allowed' : 'pointer',
                    opacity: !canSwitchFromCurrentMode('terrainCrossSection') ? 0.5 : 1
                }}
            >
                <FaChartLine />
            </button>

            {activeMode === 'terrainCrossSection' && terrainProfilePoints.length > 0 && (
                <button onClick={() => setIsTerrainModalOpen(true)} title='Generiši dijagram'>
                    <TbPresentation />
                </button>
            )}

            <button
                className={showSearch ? 'active' : ''}
                onClick={() => {
                    setShowSelectOptions(false);
                    setShowSearch(prev => !prev);
                }}
                disabled={isAnotherModeActive}
                style={{
                    cursor: isAnotherModeActive ? 'not-allowed' : 'pointer',
                    opacity: isAnotherModeActive ? 0.5 : 1
                }}
                title="Pretraga na mapi"
            >
                <TbMapPinSearch />
            </button>

            {activeMode === 'terrainCrossSection' && terrainProfilePoints.length > 0 && (
                <DynamicModal
                    isOpen={isTerrainModalOpen}
                    onRequestClose={() => {
                        setIsTerrainModalOpen(false);
                        setTerrainProfilePoints([]);
                    }}
                    title="Dijagram presijeka terena"
                    mainContent={<TerrainChart data={terrainProfilePoints} />}
                    footerContent={
                        <button
                            onClick={() => {
                                setIsTerrainModalOpen(false);
                                setTerrainProfilePoints([]);
                            }}
                            className='app-btn app-btn-secondary'
                        >
                            Zatvori
                        </button>
                    }
                />
            )}

            <DynamicModal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                title="Odaberi način crtanja"
                customWidth="600px"
                customHeight="65vh"
                customZIndex={10000}
                mainContent={
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem',
                            justifyContent: 'center',
                            minHeight: '180px'
                        }}
                    >
                        {!coordinateMode ? (
                            <>
                                <button
                                    onClick={() => handleModeChange('freeDraw')}
                                    className="drawing-options-button"
                                >
                                    Slobodno crtanje
                                </button>

                                <button
                                    onClick={() => handleModeChange('coordinates')}
                                    className="drawing-options-button"
                                >
                                    Unos koordinata
                                </button>
                            </>
                        ) : (
                            <>
                                <label>
                                    Longitude:
                                    <input
                                        type="number"
                                        value={longitude}
                                        onChange={(e) => setLongitude(e.target.value)}
                                        style={{
                                            width: "100%",
                                            padding: "8px",
                                            margin: "5px 0"
                                        }}
                                    />
                                </label>

                                <label>
                                    Latitude:
                                    <input
                                        type="number"
                                        value={latitude}
                                        onChange={(e) => setLatitude(e.target.value)}
                                        style={{
                                            width: "100%",
                                            padding: "8px",
                                            margin: "5px 0"
                                        }}
                                    />
                                </label>
                            </>
                        )}
                    </div>
                }
                footerContent={
                    <>
                        {coordinateMode && (
                            <button
                                onClick={handleCoordinateSubmit}
                                className="app-btn app-btn-success"
                                style={{ marginRight: "10px" }}
                            >
                                Potvrdi koordinate
                            </button>
                        )}

                        <button onClick={closeModal} className="app-btn app-btn-secondary">
                            Otkaži
                        </button>
                    </>
                }
            />
        </div>
    );
};

export default DrawingToolbar;